import type { GridProps, HTMLChakraProps } from '@chakra-ui/react';
import { Box, Grid, Flex, Text, VStack, Heading, Button, Tag } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import type { CustomLinksGroup } from 'types/footerLinks';

import config from 'configs/app';
import type { ResourceError } from 'lib/api/resources';
import useApiQuery from 'lib/api/useApiQuery';
import useFetch from 'lib/hooks/useFetch';
import useIssueUrl from 'lib/hooks/useIssueUrl';
import { copy } from 'lib/html-entities';
import { Link } from 'toolkit/chakra/link';
import { Skeleton } from 'toolkit/chakra/skeleton';
import IconSvg, { type IconName } from 'ui/shared/IconSvg';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
import NetworkAddToWallet from 'ui/shared/NetworkAddToWallet';

import FooterLinkItem from './FooterLinkItem';
import IntTxsIndexingStatus from './IntTxsIndexingStatus';
import getApiVersionUrl from './utils/getApiVersionUrl';

interface FooterLink {
  text: string;
  url: string;
  icon?: IconName;
  isHighlighted?: boolean;
  isExternal?: boolean;
}

interface FooterLinkGroup {
  title: string;
  links: FooterLink[];
}

const FOOTER_LINKS: Record<string, FooterLinkGroup> = {
  company: {
    title: 'Company',
    links: [
      {
        text: 'Documentation',
        url: 'https://docs.x.ink/',
      },
      {
        text: 'Discord',
        url: 'https://discord.gg/xosnetwork',
      },
      {
        text: 'Twitter',
        url: 'https://x.com/xosnetwork',
      },
    ],
  },
  community: {
    title: 'Community',
    links: [
      {
        text: 'REST API',
        url: '/api-docs',
      },
      {
        text: 'Charts',
        url: '/stats',
      },
      {
        text: 'Dapps',
        url: '/dapps',
      },
    ],
  },
  products: {
    title: 'BlockChain',
    links: [
      {
        text: 'Transactions',
        url: '/txs',
      },
      {
        text: 'Blocks',
        url: '/blocks',
      },
      {
        text: 'Internal transactions',
        url: '/internal-txs',
      },
    ],
  },
};

const AddXosButton = () => (
  <Link
    href="#"
    display="flex"
    alignItems="center"
    justifyContent="center"
    height="36px"
    width="180px"
    borderWidth="1px"
    borderColor={{ _light: '#e9ecef', _dark: 'whiteAlpha.200' }}
    borderRadius="sm"
    color="#081D35"
    transition="all 0.2s"
    px="0.25rem 0.5rem"
    bg="#e9ecef"
    _hover={{
      bg: 'gray.100',
      borderColor: 'gray.300',
      color: 'gray.700'
    }}
    _dark={{
      bg: 'gray.800',
      borderColor: 'gray.700',
      color: 'gray.400',
      _hover: {
        bg: 'gray.700',
        borderColor: 'gray.600',
        color: 'gray.300'
      }
    }}
  >
    <IconSvg name="wallets/metamask" boxSize={4} mr={2} />
    <Text fontSize="sm" fontWeight="medium">Add XOS Network</Text>
  </Link>
);

const ExternalIcon = () => (
  <IconSvg
    name="arrows/north-east"
    boxSize={3}
    ml={1}
    color="#212529"
    _dark={{ color: 'gray.400' }}
  />
);

const Footer = () => {
  const containerProps: HTMLChakraProps<'div'> = {
    as: 'footer',
    borderTopWidth: '1px',
    borderTopColor: 'border.divider',
    bg: 'rgba(248, 249, 250, 1)',
    _dark: {
      bg: 'rgba(0, 0, 0, 0.9)',
    },
  };

  const contentProps: GridProps = {
    px: { base: 4, lg: 6 },
    py: { base: 6, lg: 8 },
    maxW: `${CONTENT_MAX_WIDTH}px`,
    m: '0 auto',
  };

  return (
    <Box {...containerProps}>
      <Grid {...contentProps}>
        {/* Main Footer Content */}
        <Grid
          templateColumns={{ base: "1fr", md: "2fr 1fr 1fr 1fr" }}
          gap={{ base: 6, md: 6 }}
          mb={3}
          maxW="full"
        >
          {/* Powered By Section - Left Column */}
          <Box maxW="400px">
            <Flex direction="column" gap={4}>
              <Flex alignItems="center">
                <Text fontSize="xl" fontWeight="semibold">Powered by XOS</Text>
              </Flex>
              <Text color="gray.600" _dark={{ color: 'gray.400' }}>
                Xoscan is a Block Explorer and Analytics Platform for XOS.
              </Text>
              <Box mt={2} mb={1}>
                <NetworkAddToWallet />
              </Box>
            </Flex>
          </Box>

          {/* Links Sections */}
          {Object.values(FOOTER_LINKS).map((group) => (
            <Box key={group.title}>
              <Heading size="sm" mb={4} color="gray.800" _dark={{ color: 'gray.200' }}>
                {group.title}
              </Heading>
              <VStack align="start" gap={2}>
                {group.links.map((link) => (
                  <Link
                    key={link.text}
                    href={link.url}
                    color="gray.600"
                    _dark={{ color: 'gray.400' }}
                    _hover={{ color: 'blue.600', _dark: { color: 'blue.400' } }}
                    display="flex"
                    alignItems="center"
                    external={link.isExternal}
                    fontSize="sm"
                  >
                    {link.icon && <IconSvg name={link.icon} boxSize={4} mr={2} />}
                    {link.text}
                    {link.isExternal && <ExternalIcon />}
                  </Link>
                ))}
              </VStack>
            </Box>
          ))}
        </Grid>

        {/* Bottom Section */}
        <Flex
          borderTopWidth="1px"
          borderTopColor="border.divider"
          pt={4}
          direction="column"
          align="center"
          justify="center"
          height="15px"
        >
          <Text color="gray.600" _dark={{ color: "gray.400" }} fontSize="sm" textAlign="center">
            © {new Date().getFullYear()} Xoscan. All rights reserved
          </Text>
        </Flex>
      </Grid>
    </Box>
  );
};

export default React.memo(Footer);
